import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HowItWorksOrbit } from '@/components/HowItWorksOrbit';
import { PublicMobileMenu } from '@/components/PublicMobileMenu';
import './HowItWorksPage.css';

const PU = '#4a3aa8';

const ICONS = {
  student: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 9l10-5 10 5-10 5z" /><path d="M6 11.5V16c0 1.5 3 3 6 3s6-1.5 6-3v-4.5" /></svg>
  ),
  parent: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><circle cx="17" cy="9" r="2.5" /><path d="M17 14.5c2.5 0 4.5 2 4.5 4.5" /></svg>
  ),
  teacher: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M12 16v4M8 20h8M7 9h6M7 12h4" /></svg>
  ),
  school: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 21h18M5 21V10l7-5 7 5v11M10 21v-5h4v5" /></svg>
  ),
  ministry: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 21h18M12 3l9 6H3zM6 10v8M10 10v8M14 10v8M18 10v8" /></svg>
  ),
};

const R = [
  { icon: ICONS.student, t: 'الطالب', s: 'يتعلّم بطريقته', c: PU, d: 'تجربة تعليمية متكاملة تفهم الطالب وتوجّهه', f: ['دروس تتحول إلى قصص تفاعلية قصيرة', 'دليل تدريسي يبسّط المفاهيم والمصطلحات', 'أوراق عمل وامتحانات تغطي جوانب الدرس', 'أسئلة مفتوحة مع بناء خطوات الحل وتعزيز التفكير النقدي', 'خطة دراسية مخصصة حسب نقاط القوة والضعف'] },
  { icon: ICONS.parent, t: 'ولي الأمر', s: 'يتابع ويدعم', c: PU, d: 'نافذة واضحة على مستوى أبنائك', f: ['تقارير مبسطة عن تقدم الطالب باستمرار', 'معرفة نقاط القوة بوضوح', 'تحديد الجوانب التي تحتاج إلى دعم', 'تدخل إيجابي ومشاركة فعالة في التعلم'] },
  { icon: ICONS.teacher, t: 'المعلم', s: 'يوجّه بدقة', c: PU, d: 'منصة لإدارة العملية التعليمية بذكاء', f: ['متابعة أداء جميع الطلبة تلقائيًا', 'تحديد الموضوعات التي تحتاج إعادة شرح', 'إضافة العلامات والملاحظات ورفع الفيديوهات', 'تواصل مباشر مع الطلبة وأولياء الأمور', 'مراعاة الفروقات الفردية بين الطلبة'] },
  { icon: ICONS.school, t: 'إدارة المدرسة', s: 'تقيّم وتطوّر', c: PU, d: 'رؤية شاملة للأداء التعليمي والإداري', f: ['تحليل أداء الطلبة والمعلمين', 'تقييم جودة العملية التعليمية', 'قرارات مبنية على بيانات دقيقة لتحسين المخرجات'] },
  { icon: ICONS.ministry, t: 'الوزارة', s: 'تقرر بالبيانات', c: PU, d: 'لوحة قيادة استراتيجية للوكيل والوزير', f: ['أداء المدارس والمديريات والمحافظات بشكل لحظي', 'رصد الفجوات التعليمية وتحديد أولويات التدخل', 'تقييم أثر السياسات والبرامج بناءً على بيانات فعلية', 'توزيع الموارد حسب الاحتياج وتحديد احتياجات تدريب المعلمين'] },
];

const HowItWorksPage = () => {
  const [cur, setCur] = useState(0);
  const timer = useRef(null);
  const r = R[cur];
  const next = R[(cur + 1) % R.length];

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCur((c) => (c + 1) % R.length), 7000);
    return () => clearTimeout(timer.current);
  }, [cur]);

  return (
    <div className="hiwpage hiw-orbit-page" dir="rtl" style={{ '--c': r.c }}>
      {/* Header matching the provided design */}
      <header className="nav public-page-header" data-testid="public-page-header">
        <Link to="/" className="logo" data-testid="logo-link">RasheedGPT</Link>
        <ul className="public-desktop-navigation" data-testid="public-desktop-navigation">
          <li><Link to="/" data-testid="hiw-home-link">الرئيسية</Link></li>
          <li><Link to="/founder" data-testid="hiw-founder-link">المؤسس</Link></li>
          <li><Link to="/how-it-works" className="cur" aria-current="page" data-testid="howitworks-nav-active">كيف يعمل</Link></li>
          <li><Link to="/subscribe" data-testid="hiw-subscribe-link">اشترك معنا</Link></li>
          <li><Link to="/feedback" data-testid="hiw-feedback-link">رأيك يهمنا</Link></li>
          <li><Link to="/login" className="lg" data-testid="hiw-login-link">تسجيل الدخول</Link></li>
        </ul>
        <PublicMobileMenu />
      </header>

      {/* Decorative rings */}
      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main data-testid="howitworks-page">
        <section className="art" data-testid="hiw-illustration">
          <HowItWorksOrbit roles={R} current={cur} onSelect={setCur} />
        </section>
        <div className="col">
          <h1 className="hl" data-testid="hiw-page-title">نظام واحد.. <span className="pk">يربط الجميع</span></h1>
        <section className="card" id="hiw-role-detail" data-testid="hiw-card" style={{ '--c': r.c }}>
          <div className="pg run" key={`pg-${cur}`} data-testid="hiw-role-progress" aria-hidden="true"></div>
          <div className="body" key={`bd-${cur}`}>
            <div className="wm" aria-hidden="true">{r.icon}</div>
            <div className="hd">
              <div className="big">{r.icon}</div>
              <div>
                <em data-testid="hiw-step-counter">الخطوة {cur + 1} من {R.length}</em>
                <h2 data-testid="hiw-role-title">{r.t}</h2>
                <p data-testid="hiw-role-description">{r.d}</p>
              </div>
            </div>
            <ul className="fl">
              {r.f.map((x, j) => <li key={j} style={{ '--k': j }} data-testid={`hiw-role-feature-${j}`}>{x}</li>)}
            </ul>
            <button className="nx" style={{ '--c': next.c }} onClick={() => setCur((cur + 1) % R.length)} data-testid="hiw-next-btn">
              التالي: {next.t}
            </button>
          </div>
        </section>
        </div>
      </main>
    </div>
  );
};

export default HowItWorksPage;
